import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { ApmTranslatePipe } from '@mitsubishi/core';
import { ApmResourceIds } from '@mitsubishi/shared/data-access';
import { ButtonComponent, SectionTitleComponent, SlideToggleComponent } from '@mitsubishi/shared/ui';
import { InlineSpinnerComponent } from '@mitsubishi/ui/spinner';

import { FreestDatasheetPdfRequest, OptionalSection } from '../../models/chiller-free-st.model';
import { ChillerFreeStService } from '../../services/chiller-free-st.service';

export interface ChillerFreeStDatasheetSettingsDialogData {
  selectedSections: OptionalSection[];
  datasheetPdfRequest: FreestDatasheetPdfRequest;
  projectId: string;
}

@Component({
  selector: 'apm-chiller-free-st-datasheet-settings-dialog',
  templateUrl: './chiller-free-st-datasheet-settings-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ApmTranslatePipe, ButtonComponent, SlideToggleComponent, InlineSpinnerComponent, SectionTitleComponent],
})
export class ChillerFreeStDatasheetSettingsDialogComponent {
  readonly #dialogData = inject(MAT_DIALOG_DATA) as ChillerFreeStDatasheetSettingsDialogData;
  readonly #dialogRef = inject(MatDialogRef<ChillerFreeStDatasheetSettingsDialogComponent>);
  readonly #chillerFreeStService = inject(ChillerFreeStService);

  availableSectionsResource = rxResource({
    stream: () =>
      this.#chillerFreeStService.getDatasheetPdfAvailableSections(
        this.#dialogData.projectId,
        this.#dialogData.datasheetPdfRequest
      ),
  });

  readonly RES = ApmResourceIds;

  readonly partLoadData = signal(this.#dialogData.selectedSections.includes('PART_LOAD_DATA'));
  readonly iplvGb = signal(this.#dialogData.selectedSections.includes('IPLV_GB'));
  readonly accop = signal(this.#dialogData.selectedSections.includes('ACCOP'));

  readonly showPartLoadData = computed(() => this.availableSectionsResource.value()?.PART_LOAD_DATA ?? false);
  readonly showIplv = computed(() => this.availableSectionsResource.value()?.IPLV ?? false);
  readonly showAccop = computed(() => this.availableSectionsResource.value()?.ACCOP ?? false);

  save(): void {
    const sections: OptionalSection[] = [];
    if (this.showPartLoadData() && this.partLoadData()) sections.push('PART_LOAD_DATA');
    if (this.showIplv() && this.iplvGb()) sections.push('IPLV_GB');
    if (this.showAccop() && this.accop()) sections.push('ACCOP');
    this.#dialogRef.close(sections);
  }

  cancel(): void {
    this.#dialogRef.close();
  }
}
